import React, { useState, useEffect } from 'react';
import { fmtDate } from '../../lib/dates';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Loader2, FileText, Download, Mail, Gavel, BookMarked } from 'lucide-react';
import { runAiJob } from '../../lib/aiJob';
import { BriefRulings } from './BriefRulings';
import { BriefVersions } from './BriefVersions';
import { BriefApproval } from './BriefApproval';

const KINDS = [['descargos', 'Descargos (sumario disciplinario)'], ['respuesta_oficio', 'Respuesta a oficio (notario o CBR)'], ['defensa_denuncia', 'Defensa ante denuncia'], ['recurso', 'Recurso'], ['contestacion', 'Contestación de demanda']];
const LIBRARY_KINDS = ['respuesta_oficio', 'defensa_denuncia', 'descargos'];
const MIME = { docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', pdf: 'application/pdf' };

// Same placement as the Word/PDF export: right after the «Doctrina» section, else at the end
const withRulings = (b) => {
  const rul = (b.doctrine_rulings || []).filter((r) => !r.removed).map((r) => r.parrafo);
  const contra = (b.contra_rulings || []).filter((r) => !r.removed).map((r) => r.parrafo);
  let secs = b.secciones || [];
  if (rul.length) {
    const block = { titulo: 'JURISPRUDENCIA QUE RESPALDA LA DOCTRINA', parrafos: rul, rulings: true };
    const i = secs.findIndex((s) => /doctrina/i.test(s.titulo || ''));
    secs = i < 0 ? [...secs, block] : [...secs.slice(0, i + 1), block, ...secs.slice(i + 1)];
  }
  const sala = (b.sala_rulings || []).filter((r) => !r.removed).map((r) => r.parrafo);
  if (sala.length) secs = [...secs, { titulo: 'JURISPRUDENCIA DE LA SALA', parrafos: sala, rulings: true }];
  return contra.length ? [...secs, { titulo: 'SOBRE LA JURISPRUDENCIA CONTRARIA', parrafos: contra, contra: true }] : secs;
};

export const guessKind = (title = '') => {
  const t = title.toLowerCase();
  if (/contest/.test(t)) return 'contestacion';
  if (/recurso|apelac|casac|reposic|protecc|queja|nulidad/.test(t)) return 'recurso';
  return 'descargos';
};

export const BriefDialog = ({ matter, open, onOpenChange, onUpdated = () => {}, fromRehearsal = false, deadline = null }) => {
  const { api } = useAuth();
  const [brief, setBrief] = useState(deadline ? null : matter.legal_brief || null);
  const [kind, setKind] = useState(deadline ? guessKind(deadline.title) : fromRehearsal ? 'descargos' : matter.legal_brief?.kind || (matter.matter_type === 'disciplinario' ? 'descargos' : matter.assigned_judge_id ? 'recurso' : 'contestacion'));
  const [lawyer, setLawyer] = useState('');
  const [lang, setLangRaw] = useState({ brasil: 'pt', eeuu: 'en' }[matter.country] || 'es');
  const [langTouched, setLangTouched] = useState(false);
  const setLang = (v) => { setLangRaw(v); setLangTouched(true); };
  const [busy, setBusy] = useState(false);
  const [adapt, setAdapt] = useState(true);
  const saveLibrary = async () => {
    try { await api.post(`/courts/matters/${matter.id}/library`); toast.success('Guardada en tu Biblioteca de Oficios'); }
    catch (e) { toast.error(e.response?.data?.detail || 'No se pudo guardar'); }
  };

  const draft = async () => {
    setBusy(true);
    try {
      setBrief(await runAiJob(api, `/crm/matters/${matter.id}/brief`, { kind, lawyer: lawyer || null, deadline_id: deadline?.id || null, lang: langTouched || matter.country ? lang : null, adapt_to_judge: adapt }, { timeout: 570000 }));
      onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'No se pudo redactar');
    } finally { setBusy(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (fromRehearsal) draft(); }, []);
  useEffect(() => {
    if (deadline) api.get(`/crm/matters/${matter.id}/deadline-brief/${deadline.id}`).then((r) => setBrief(r.data.brief)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [sending, setSending] = useState(false);
  const emailClient = async () => {
    setSending(true);
    try {
      const r = await api.post(`/crm/matters/${matter.id}/brief/email`, { deadline_id: deadline?.id || null });
      toast.success(`Enviado a ${r.data.sent_to} (${r.data.email})`);
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo enviar el correo'); } finally { setSending(false); }
  };

  const download = async (fmt) => {
    try {
      const r = await api.get(`/crm/matters/${matter.id}/brief/${fmt}`, { responseType: 'blob', params: deadline ? { deadline_id: deadline.id } : {} });
      const url = URL.createObjectURL(new Blob([r.data], { type: MIME[fmt] }));
      const a = document.createElement('a');
      a.href = url; a.download = `${brief?.kind || kind}-${(matter.title || 'escrito').replace(/[\\/:*?"<>|]/g, '-')}.${fmt}`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('No se pudo descargar'); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="brief-dialog" className="bg-slate-950 border-slate-700 text-white max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white">Redactar escrito</DialogTitle>
          <DialogDescription className="text-slate-400">Usa el expediente, la estrategia validada, tu último ensayo y las lecciones de los expertos.</DialogDescription>
        </DialogHeader>
        {fromRehearsal && (
          <p data-testid="brief-from-rehearsal-note" className="text-xs bg-purple-500/10 border border-purple-500/30 text-purple-200 rounded-lg px-3 py-2">
            Este escrito incorpora las mejoras y respuestas modelo que el juez IA te dio en tu último ensayo.
          </p>
        )}
        {deadline && (
          <p data-testid="brief-deadline-note" className="text-xs bg-amber-500/10 border border-amber-500/30 text-amber-200 rounded-lg px-3 py-2">
            Escrito para «{deadline.title}» · vence {deadline.due_date ? fmtDate(deadline.due_date) : 'sin fecha (falta la notificación)'}
            {deadline.legal_basis ? ` · ${deadline.legal_basis}` : ''}. El tipo se detectó del plazo; puedes cambiarlo.
          </p>
        )}
        {(matter.assigned_judge_id || ['apelacion', 'casacion', 'disciplinaria'].includes(matter.instance)) && (
          <label data-testid="brief-adapt-toggle" className="flex items-center gap-2 text-xs bg-emerald-500/10 border border-emerald-500/30 text-emerald-100 rounded-lg px-3 py-2 cursor-pointer">
            <input data-testid="brief-adapt-checkbox" type="checkbox" checked={adapt} onChange={(e) => setAdapt(e.target.checked)} className="accent-emerald-500" />
            <Gavel className="w-3.5 h-3.5 text-emerald-300" />Ministro en el Alegato: ajustar tono y argumentos al perfil de <b>{matter.instance === 'disciplinaria' ? (matter.fiscal_id ? 'el fiscal judicial a cargo' : 'la fiscalía judicial de la Corte') : ['apelacion', 'casacion'].includes(matter.instance) ? (matter.sala_ids?.length ? `la sala (${matter.sala_ids.length} ministros)` : 'la Corte en su conjunto') : matter.assigned_judge_name}</b>
          </label>
        )}
        <div className="grid md:grid-cols-4 gap-3">
          <select data-testid="brief-lang-select" value={lang} onChange={(e) => setLang(e.target.value)} title="Idioma del escrito (según país del caso)" className="bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm">
            <option value="es">Español</option><option value="pt">Português</option><option value="en">English</option>
          </select>
          <select data-testid="brief-kind-select" value={kind} onChange={(e) => setKind(e.target.value)} className="bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm">
            {KINDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <input data-testid="brief-lawyer-input" value={lawyer} onChange={(e) => setLawyer(e.target.value)} placeholder="Abogado que firma (opcional)" className="bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm" />
          <button data-testid="brief-draft-btn" onClick={draft} disabled={busy} className="bg-amber-500 text-slate-950 font-semibold rounded-lg py-2 text-sm flex items-center justify-center gap-2 disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}{busy ? 'Redactando (2-6 min)...' : brief ? 'Redactar de nuevo' : 'Redactar'}
          </button>
        </div>
        {brief && !busy && (
          <div data-testid="brief-preview" className="mt-4 space-y-3">
            <div className="flex gap-2">
              <button data-testid="brief-docx-btn" onClick={() => download('docx')} className="text-xs bg-blue-600 text-white rounded-full px-3 py-1.5 flex items-center gap-1"><Download className="w-3 h-3" />Word editable</button>
              <button data-testid="brief-pdf-btn" onClick={() => download('pdf')} className="text-xs bg-slate-700 text-white rounded-full px-3 py-1.5 flex items-center gap-1"><Download className="w-3 h-3" />PDF</button>
              <button data-testid="brief-email-btn" onClick={emailClient} disabled={sending} className="text-xs bg-emerald-600 text-white rounded-full px-3 py-1.5 flex items-center gap-1 disabled:opacity-50">{sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}Enviar al cliente</button>
              {LIBRARY_KINDS.includes(brief.kind) && !deadline && <button data-testid="brief-library-btn" onClick={saveLibrary} className="text-xs border border-amber-500/60 text-amber-300 rounded-full px-3 py-1.5 flex items-center gap-1 hover:bg-amber-500/10"><BookMarked className="w-3 h-3" />Funcionó: guardar en Biblioteca</button>}
              <BriefVersions matter={matter} deadline={deadline} onRestored={(b) => { setBrief(b); onUpdated(); }} />
            </div>
            <BriefApproval matter={matter} deadline={deadline} />
            {brief.doctrine_used?.length > 0 && (
              <p data-testid="brief-doctrine-used" className="text-xs text-sky-200 bg-sky-500/10 border border-sky-500/30 rounded-lg px-3 py-2"><BookMarked className="w-3 h-3 inline mr-1" />Doctrina consultada (aparte de la jurisprudencia): {Object.entries(brief.doctrine_used.reduce((acc, x) => ({ ...acc, [`${x.author || 'Ignacio Vidal Domínguez'}, «${x.work}»`]: [...(acc[`${x.author || 'Ignacio Vidal Domínguez'}, «${x.work}»`] || []), x.page] }), {})).map(([w, ps]) => `${w}, p${ps.length > 1 ? 'p' : ''}. ${[...new Set(ps)].sort((a, b) => a - b).join(', ')}`).join(' · ')}</p>
            )}
            <BriefRulings matter={matter} brief={brief} deadline={deadline} onChange={(patch) => { setBrief({ ...brief, ...patch }); onUpdated(); }} />
            {brief.library_used?.length > 0 && (
              <p data-testid="brief-library-used" className="text-xs text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2"><BookMarked className="w-3 h-3 inline mr-1" />Fidem reutilizó {brief.library_used.length} respuesta(s) ganadora(s) de tu Biblioteca: {brief.library_used.map((x) => x.title).join(' · ')}</p>
            )}
            {brief.judge_adaptation && (
              <div data-testid="brief-judge-adaptation" className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 text-sm">
                <p className="text-emerald-300 font-semibold mb-1 flex items-center gap-1.5"><Gavel className="w-4 h-4" />Ajustado a {brief.judge_adaptation.judge_name} ({brief.judge_adaptation.position}{brief.judge_adaptation.estilo ? ` · estilo ${brief.judge_adaptation.estilo}` : ''}){brief.judge_adaptation.members?.length ? `: ${brief.judge_adaptation.members.join(', ')}` : ''}</p>
                <ul className="space-y-1 text-slate-300">{(brief.judge_adaptation.cambios || []).map((c, i) => <li key={i}>• {c}</li>)}</ul>
              </div>
            )}
            <div className="bg-white text-slate-900 rounded-xl p-6 font-serif text-sm leading-relaxed space-y-3">
              <p className="text-right font-bold">{brief.suma}</p>
              <p className="font-bold">{brief.destinatario}</p>
              <p className="text-justify">{brief.comparecencia}</p>
              {withRulings(brief).map((s, i) => (
                <div key={i} data-testid={s.rulings ? 'brief-rulings-section' : s.contra ? 'brief-contra-section' : undefined}><p className="font-bold">{s.titulo}</p>{(s.parrafos || []).map((p, j) => <p key={j} className="text-justify mt-1">{p}</p>)}</div>
              ))}
              <p className="text-justify">{brief.peticion}</p>
              {(brief.otrosies || []).map((o, i) => <p key={i} className="text-justify"><b>{o.titulo}:</b> {o.texto}</p>)}
              <p className="text-center pt-6">{brief.firma}</p>
              {brief.notas_al_pie?.length > 0 && (
                <div data-testid="brief-footnotes" className="border-t border-slate-400 pt-3 text-xs">
                  <p className="font-bold mb-1">NOTAS</p>
                  {brief.notas_al_pie.map((n, i) => <p key={i} className="mt-1">{n}</p>)}
                </div>
              )}
            </div>
            {brief.notas_para_el_abogado?.length > 0 && (
              <div data-testid="brief-notes" className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 text-sm">
                <p className="text-amber-300 font-semibold mb-1">Antes de presentar</p>
                <ul className="space-y-1 text-slate-300">{brief.notas_para_el_abogado.map((n, i) => <li key={i}>• {n}</li>)}</ul>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
